import type { TerrainSampler, TilePos } from "../world/types";
import { type Aabb, moveAabb } from "./collision";

/*
 * The walker: a camera on legs.
 *
 * The player's transform lives here, in the client, for the whole of Phase 1
 * (docs/plans/phase-1-plan.md, decision 7 — there is no player entity in the sim
 * yet). The controller collides against terrain queries and reports its tile
 * back to the world as interest; that is the entire relationship.
 *
 * Two things are deliberately simple, because neither is what Phase 1 is
 * testing:
 *
 * - **No acceleration model.** Horizontal velocity is set straight from the
 *   input each tick, on the ground and in the air alike. Momentum, friction and
 *   air control are feel work, and feel work belongs after Gate A's art
 *   checkpoint, not before the world it would be tuned against exists.
 * - **Look is not simulated.** Yaw and pitch are applied at frame rate from raw
 *   mouse deltas, while position is stepped at `TICK_HZ` and interpolated.
 *   Latency on look is the single most perceptible thing in a first-person game;
 *   latency on position, at 30 Hz with interpolation, is not perceptible at all.
 */

/**
 * Half the player's width and depth, in tiles. A 0.6-tile-square footprint.
 *
 * Exported since wave 1.5 for {@link EYE_HEIGHT}'s reason, one step further on:
 * the screenshot vantage does not only aim the camera now, it checks that a
 * *body* fits where it is about to stand (`witness/witness.ts`), and a body is
 * this box. A second copy of the footprint would be a vantage that clears a gap
 * the player then cannot occupy.
 */
export const BODY_HALF_WIDTH = 0.3;

/** Player height in tiles. Exported with {@link BODY_HALF_WIDTH}, for its reason. */
export const BODY_HEIGHT = 1.8;

/**
 * The eye's height above the feet, in tiles.
 *
 * Exported because anything that aims the camera at something has to know where
 * the camera *is* — the dev handle's screenshot vantage computes a pitch from
 * it (`witness/witness.ts`), and a second copy of the number would be a second
 * copy that drifts.
 */
export const EYE_HEIGHT = 1.62;

/** Horizontal speed in tiles per second. */
const WALK_SPEED = 4.5;

/**
 * What is left of the walk when the player's legs will not hold them.
 *
 * §7.5's *"all `STANCE` lost → crawl instead of walk"*, and this is the client's
 * half of it. **The seam is worth stating**: the player's gait is host physics —
 * the simulation has never moved their body (Phase 3 decision 10) — so the sim's
 * verdict crosses as a bit (`sim_core::Afflictions::OFF_ITS_FEET`) and the host
 * applies it to its own constant. That is presentation of a sim fact rather than
 * a consequence computed client-side, and the difference is that nothing here
 * decides how hurt anybody is.
 *
 * It is applied to the *intent* rather than to the speed (`main.ts`), because
 * the intent is already a vector this controller normalises: a scale on it is
 * one line at one call site rather than a second speed constant to keep in step.
 *
 * PROVISIONAL, and a taste number about pace: a third is a crawl that still gets
 * somewhere, which is what the sim's own answer to no-stance is — the local
 * tier's bodies stop walking altogether, and a *player* who could not move at
 * all would be a session with nothing to do but watch.
 */
export const CRAWL_INTENT_SCALE = 1 / 3;

/**
 * Horizontal speed while sprinting, in tiles per second.
 *
 * Fast enough that crossing town stops being a cost, slow enough that the
 * world still goes past as a place rather than as scenery. PROVISIONAL along
 * with the rest of the movement numbers.
 */
const RUN_SPEED = 7.5;

/** Downward acceleration in tiles per second squared. */
const GRAVITY = 26;

/**
 * Upward speed at the moment of a jump, in tiles per second.
 *
 * Apex is `JUMP_SPEED^2 / (2 * GRAVITY)` = 1.23 tiles, which is the number that
 * matters: it clears a one-tile step with enough margin that a player never has
 * to think about it, and falls short of two tiles so that terrain still reads as
 * an obstacle. PROVISIONAL along with the rest of the movement numbers.
 */
const JUMP_SPEED = 8;

/** The fastest the player falls, in tiles per second. Keeps a long drop bounded. */
const TERMINAL_FALL_SPEED = 30;

/** Falling past this z means the world has a hole in it; the player is put back at spawn. */
const VOID_Z = -64;

/** What the player wants to do this tick. Produced by `input/controls.ts`. */
export interface MoveIntent {
  /** Forward/backward axis in -1..1, relative to where the player is looking. */
  readonly forward: number;

  /** Right/left axis in -1..1, relative to where the player is looking. */
  readonly strafe: number;

  /** A jump was requested since the last tick. */
  readonly jump: boolean;

  /** Sprint is held: the horizontal move runs at {@link RUN_SPEED}. */
  readonly sprint: boolean;
}

/**
 * Turns a walk intention into a horizontal direction, given where the player is
 * looking.
 *
 * **The client's one conversion from an intent to a heading**, and it is a
 * function rather than four lines inside {@link PlayerController.step} because
 * chunk Fx-3 gave it a second caller: a dev flight moves by the same keys in the
 * same frame of reference and only ignores the terrain (`dev/flight.ts`). Two
 * copies of it would be two answers to *"which way is forward"*, and the bug
 * that produces is a tool whose strafe is mirrored.
 *
 * Yaw 0 looks along +y, so forward is `(-sin, cos)` and right is `(cos, sin)` —
 * the same rotation the camera is built from in `render/scene.ts`. The result is
 * normalised when it is longer than one, so walking diagonally is not 41 %
 * faster than walking straight; the guard is what keeps a zero vector from
 * normalising into a NaN that would propagate into the position and never come
 * out.
 */
export function moveVector(
  yaw: number,
  forward: number,
  strafe: number,
): { x: number; y: number } {
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  const x = -sin * forward + cos * strafe;
  const y = cos * forward + sin * strafe;
  const length = Math.hypot(x, y);

  return length > 1 ? { x: x / length, y: y / length } : { x, y };
}

/** Where to put the camera for a frame. */
export interface CameraPose {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly pitch: number;
}

/** Where the player is, for the HUD. */
export interface PlayerPosition {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export class PlayerController {
  private readonly terrain: TerrainSampler;
  private readonly spawn: PlayerPosition;

  /** Feet centre: the box's horizontal middle and its bottom face. */
  private x: number;
  private y: number;
  private z: number;

  /** The same, as of the previous tick — the other end of the render interpolation. */
  private previousX: number;
  private previousY: number;
  private previousZ: number;

  private velocityZ = 0;
  private onGround = false;

  /** Radians. Yaw 0 looks along +y (north); positive yaw turns left. */
  private yaw = 0;
  private pitch = 0;

  /** Reused across ticks: the collision routine mutates it in place. */
  private readonly box: Aabb = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };

  constructor(terrain: TerrainSampler, spawn: PlayerPosition) {
    this.terrain = terrain;
    this.spawn = spawn;
    this.x = spawn.x;
    this.y = spawn.y;
    this.z = spawn.z;
    this.previousX = spawn.x;
    this.previousY = spawn.y;
    this.previousZ = spawn.z;
  }

  get position(): PlayerPosition {
    return { x: this.x, y: this.y, z: this.z };
  }

  get standing(): boolean {
    return this.onGround;
  }

  /** The tile the player is standing in — what the world is told to keep loaded. */
  get tile(): TilePos {
    return { x: Math.floor(this.x), y: Math.floor(this.y), z: Math.floor(this.z) };
  }

  /**
   * Turns the view by mouse deltas, in radians.
   *
   * Called at frame rate, not tick rate (see the header). Pitch is clamped just
   * short of straight up and straight down: at exactly ±90° the forward vector
   * loses its horizontal component and the walk direction becomes undefined.
   */
  look(deltaYaw: number, deltaPitch: number): void {
    const limit = Math.PI / 2 - 0.01;

    this.yaw += deltaYaw;
    this.pitch = Math.min(limit, Math.max(-limit, this.pitch + deltaPitch));

    // Kept in -PI..PI so it stays exact after an hour of spinning in one
    // direction; a float that has grown to a million radians has lost most of
    // its precision to the integer part.
    if (this.yaw > Math.PI) {
      this.yaw -= 2 * Math.PI;
    } else if (this.yaw < -Math.PI) {
      this.yaw += 2 * Math.PI;
    }
  }

  /** Advances the player by one fixed tick. */
  step(stepSeconds: number, intent: MoveIntent): void {
    this.previousX = this.x;
    this.previousY = this.y;
    this.previousZ = this.z;

    // Where the intent points, in world axes: {@link moveVector} owns the
    // rotation and the diagonal normalisation, because the flight tool steers
    // by the same rule and there must be one of it.
    const { x: moveX, y: moveY } = moveVector(this.yaw, intent.forward, intent.strafe);

    if (intent.jump && this.onGround) {
      this.velocityZ = JUMP_SPEED;
      this.onGround = false;
    }

    this.velocityZ = Math.max(-TERMINAL_FALL_SPEED, this.velocityZ - GRAVITY * stepSeconds);

    // Sprint scales the horizontal move and nothing else: the jump, gravity
    // and the fall cap belong to the body, not to how hard it is pushed.
    const speed = intent.sprint ? RUN_SPEED : WALK_SPEED;

    this.syncBoxFromPosition();
    const outcome = moveAabb(
      this.box,
      moveX * speed * stepSeconds,
      moveY * speed * stepSeconds,
      this.velocityZ * stepSeconds,
      this.terrain,
    );
    this.syncPositionFromBox();

    if (outcome.blockedZ) {
      // Whatever stopped the fall or the jump also spends the vertical speed;
      // keeping it would teleport the player through the surface next tick.
      this.velocityZ = 0;
    }

    this.onGround = outcome.landed;

    if (this.z < VOID_Z) {
      this.respawn();
    }
  }

  /**
   * Returns where to put the camera for a frame `alpha` of the way between the
   * last tick and the next.
   *
   * Interpolating position while reading orientation live is the whole point of
   * the split: the world moves smoothly at whatever the display runs at, and the
   * view answers the mouse immediately.
   */
  pose(alpha: number): CameraPose {
    return {
      x: this.previousX + (this.x - this.previousX) * alpha,
      y: this.previousY + (this.y - this.previousY) * alpha,
      z: this.previousZ + (this.z - this.previousZ) * alpha + EYE_HEIGHT,
      yaw: this.yaw,
      pitch: this.pitch,
    };
  }

  /**
   * Puts the player somewhere, facing somewhere, at rest.
   *
   * **Development only, and the one thing in this client a player cannot do.**
   * It exists because the art pass is judged on screenshots (the Gate A
   * precedent, `docs/plans/gate-a.md`) and aiming one by walking is an exercise
   * in frame-timing rather than in aiming: a headless driver needs to be able to
   * say "stand in front of the grave and look at it" and get the same picture
   * twice. `dev/handle.ts` is the only caller and it is absent from the
   * production bundle.
   *
   * It writes nothing but this client's own transform. The one effect it has on
   * the simulation is the ordinary one every step has — the next tick's interest
   * command names the tile the player is now standing in — so a session that
   * used it still records and verifies as a replay of what actually happened.
   */
  placeAt(position: PlayerPosition, yaw: number, pitch: number): void {
    this.x = position.x;
    this.y = position.y;
    this.z = position.z;
    // Both ends of the interpolation, or the frame after the jump draws the
    // camera somewhere between here and where the player used to be.
    this.previousX = this.x;
    this.previousY = this.y;
    this.previousZ = this.z;
    this.velocityZ = 0;
    this.onGround = false;
    this.yaw = 0;
    this.pitch = 0;
    // Through `look`, so the pitch clamp and the yaw wrap are applied exactly
    // once and in one place.
    this.look(yaw, pitch);
  }

  /**
   * Moves the body to a position with no collision, no gravity and no ground.
   *
   * **Development only, and the third time this class has bent its own rule** —
   * after {@link PlayerController.placeAt}, whose header carries the argument.
   * The dev flight tool is the one caller (`dev/flight.ts`, chunk Fx-3: the
   * owner's *"I also need a debug option for flight/no clip"*), and it is a
   * *seam* rather than a feature: the rule about how fast a flight moves and
   * which keys lift it lives over there, and what lives here is the one thing
   * only this class can do — write the transform without asking
   * `player/collision.ts` about it.
   *
   * It differs from `placeAt` in the two ways a per-tick move has to. **The
   * previous position is left where the last tick put it**, so the frame
   * interpolation still has two ends and a flight glides instead of stepping
   * thirty times a second ({@link PlayerController.pose}); and **the view is not
   * touched at all**, because a flight is steered by looking.
   *
   * The vertical speed is spent and the body is off the ground, which is what
   * makes switching flight back off land rather than resume: gravity starts from
   * rest under a body that has been told it is standing on nothing.
   */
  glide(x: number, y: number, z: number): void {
    this.previousX = this.x;
    this.previousY = this.y;
    this.previousZ = this.z;
    this.x = x;
    this.y = y;
    this.z = z;
    this.velocityZ = 0;
    this.onGround = false;
  }

  /** Puts the player back at the spawn point, at rest. */
  respawn(): void {
    this.x = this.spawn.x;
    this.y = this.spawn.y;
    this.z = this.spawn.z;
    this.previousX = this.x;
    this.previousY = this.y;
    this.previousZ = this.z;
    this.velocityZ = 0;
    this.onGround = false;
  }

  private syncBoxFromPosition(): void {
    this.box.minX = this.x - BODY_HALF_WIDTH;
    this.box.maxX = this.x + BODY_HALF_WIDTH;
    this.box.minY = this.y - BODY_HALF_WIDTH;
    this.box.maxY = this.y + BODY_HALF_WIDTH;
    this.box.minZ = this.z;
    this.box.maxZ = this.z + BODY_HEIGHT;
  }

  private syncPositionFromBox(): void {
    this.x = (this.box.minX + this.box.maxX) / 2;
    this.y = (this.box.minY + this.box.maxY) / 2;
    this.z = this.box.minZ;
  }
}
